import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api, { paymentBatchApi } from '../api';
import {
    PageHeader,
    Card,
    Button,
    Table,
    LoadingSpinner,
    Modal,
    Pagination
} from '../components/UI';
import Select from '../components/Select';
import * as I from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const PaymentPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [payments, setPayments] = useState([]); // Represents invoices fetched for payment
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('approved');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalPayments, setTotalPayments] = useState(0);
    const [pageSize] = useState(10);
    const [vendorMap, setVendorMap] = useState({});

    // Selection state for batch creation
    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
    const [creatingBatch, setCreatingBatch] = useState(false);

    // Store only selectedPaymentId
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);
    const selectedPayment = payments.find(p => p.id === selectedPaymentId) || null;

    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [paymentDate, setPaymentDate] = useState(() => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    });
    const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
    const [referenceNumber, setReferenceNumber] = useState('');

    const referenceInputRef = useRef(null);
    const referenceValueRef = useRef('');

    useEffect(() => {
        if (showPaymentModal) {
            setTimeout(() => referenceInputRef.current?.focus(), 0);
        }
    }, [showPaymentModal, selectedPaymentId]);

    const isPaidStatus = (status) => {
        if (!status) return false;
        const s = status.toLowerCase();
        return ['paid', 'invoice_paid', 'completed', 'tds_paid', 'in_payment_batch'].includes(s);
    };

    const fetchVendorNames = useCallback(async (vendorIds) => {
        try {
            const uniqueVendorIds = [...new Set(vendorIds.filter(id => id && !vendorMap[id]))];
            if (uniqueVendorIds.length === 0) return;

            const token = localStorage.getItem('token');
            if (!token) return;

            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            try {
                const response = await api.get('/vendors', config);
                if (response.data && Array.isArray(response.data.content)) {
                    const vendorMapFromResponse = response.data.content.reduce((acc, vendor) => {
                        if (vendor.id && vendor.name) acc[vendor.id] = vendor.name;
                        return acc;
                    }, {});
                    const newVendorMap = {};
                    uniqueVendorIds.forEach(id => {
                        newVendorMap[id] = vendorMapFromResponse[id] || `Vendor ${id}`;
                    });
                    setVendorMap(prev => ({ ...prev, ...newVendorMap }));
                    return;
                }
            } catch (err) {
                const vendorPromises = uniqueVendorIds.map(id =>
                    api.get(`/vendors/${id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
                        .then(r => ({ id: id.toString(), name: r.data?.name || `Vendor ${id}` }))
                        .catch(() => ({ id: id.toString(), name: `Vendor ${id}` }))
                );
                const results = await Promise.all(vendorPromises);
                const fallbackMap = {};
                results.forEach(({ id, name }) => fallbackMap[id] = name);
                setVendorMap(prev => ({ ...prev, ...fallbackMap }));
            }
        } catch (err) {
            console.error('Error in fetchVendorNames:', err);
        }
    }, [vendorMap]);

    const toBackendStatus = (frontendStatus) => {
        if (!frontendStatus) return null;
        const map = {
            'approved': 'APPROVED',
            'invoice_paid': 'INVOICE_PAID'
        };
        const normalized = String(frontendStatus).toLowerCase();
        return map[normalized] || normalized.toUpperCase();
    };

    const fetchPayments = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            params.append('page', currentPage);
            params.append('size', pageSize);

            const backendStatus = toBackendStatus(filterStatus);
            if (backendStatus) {
                params.append('status', backendStatus);
            }

            params.append('sort', 'lastActivityAt,desc');

            const response = await api.get(`/invoices?${params.toString()}`);
            const responseData = (response.data.content || []).filter(
                invoice => invoice.status !== 'REJECTED' && invoice.status !== 'rejected'
            );

            const totalElements = response.data.totalElements || responseData.length;
            const totalPagesResult = response.data.totalPages || Math.ceil(totalElements / pageSize);

            const paymentData = responseData.map(inv => ({
                id: inv.id,
                invoiceNumber: inv.invoiceNumber,
                vendorId: inv.vendorId,
                amount: inv.totalAmount != null ? Number(inv.totalAmount) : 0,
                tdsAmount: inv.tdsAmount != null ? Number(inv.tdsAmount) : null,
                tdsPercentage: inv.tdsPercentage != null ? Number(inv.tdsPercentage) : null,
                payableAmount: inv.invoicePayable != null ? Number(inv.invoicePayable) : null,
                status: inv.paymentBatchId ? 'in_payment_batch' : inv.status?.toLowerCase(),
                paymentBatchId: inv.paymentBatchId,
                dueDate: inv.dueDate,
                dateReceived: inv.dateReceived || inv.receivedDateTime || null,
                invoiceDate: inv.invoiceDate || null,
                subtotal: inv.subtotal != null ? Number(inv.subtotal) : null,
                tax: inv.tax != null ? Number(inv.tax) : null,
                paymentDate: inv.invoicePaidDate || inv.invoicePaidTime || null,
                paymentMethod: inv.invoicePaymentMethod || null,
                referenceNumber: inv.invoicePaymentReference || null,
                remarks: null
            }));

            setPayments(paymentData);
            setTotalPayments(totalElements);
            setTotalPages(totalPagesResult);
            // Reset selection on fetch/page change if desired, or keep it.
            // Keeping it might be tricky across pages if we don't track all.
            // For now, let's keep selection state valid only for visible items or manage globally.
            // But user might want to select across pages? 
            // Simple approach: Reset selection on filter change, but maybe keep on page change?
            // To be safe, let's NOT reset on page change automatically to allow cross-page selection?
            // But standard tables usually reset. Let's keep it simple for now.

            const vendorIds = paymentData.map(p => p.vendorId).filter(Boolean);
            if (vendorIds.length > 0) fetchVendorNames(vendorIds);

        } catch (err) {
            setError('Failed to fetch payments. Please ensure backend services are running.');
            console.error("Fetch Payments Error:", err);
            setPayments([]);
            setTotalPayments(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filterStatus, fetchVendorNames]);

    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    const filteredPayments = payments.filter(payment => {
        const vendorName = vendorMap[payment.vendorId] || '';
        if (!searchTerm) return true;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return (
            (payment.invoiceNumber && payment.invoiceNumber.toLowerCase().includes(lowerSearchTerm)) ||
            (vendorName && vendorName.toLowerCase().includes(lowerSearchTerm))
        );
    });

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleFilterChange = (e) => {
        const newStatus = e.target.value;
        setFilterStatus(newStatus);
        setCurrentPage(0);
        setSelectedInvoiceIds(new Set()); // Reset selection on filter change
    };

    const handlePaymentClick = (payment) => {
        setSelectedPaymentId(payment.id);
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        setPaymentDate(`${yyyy}-${mm}-${dd}T${hh}:${mi}`);
        setPaymentMethod('bank_transfer');
        referenceValueRef.current = '';
        setReferenceNumber('');
        setShowPaymentModal(true);
    };

    const handlePaymentSubmit = async () => {
        if (!selectedPayment || processingPayment) return;

        if (selectedPayment.payableAmount == null) {
            toast.error('Invoice payable amount is missing. Cannot process payment.');
            return;
        }

        const confirmed = await confirmProcessPayment(selectedPayment.payableAmount);
        if (!confirmed) return;

        setProcessingPayment(true);

        try {
            const payload = {
                invoicePaidDate: paymentDate || null,
                invoicePaymentMethod: paymentMethod,
                invoicePaymentReference: referenceValueRef.current || referenceNumber || null
            };

            const resp = await api.put(`/invoices/${selectedPayment.id}/status/pay`, payload);
            const updatedInvoice = resp.data;

            setPayments(prevPayments => prevPayments.map(p =>
                p.id === selectedPayment.id
                    ? {
                        ...p,
                        status: (updatedInvoice.status || 'INVOICE_PAID').toString().toLowerCase(),
                        paymentDate: updatedInvoice.invoicePaidDate || updatedInvoice.invoicePaidTime || paymentDate,
                        paymentMethod: updatedInvoice.invoicePaymentMethod || paymentMethod,
                        referenceNumber: updatedInvoice.invoicePaymentReference || referenceValueRef.current || referenceNumber || null
                    }
                    : p
            ));

            toast.success('Payment processed successfully!');
            setShowPaymentModal(false);
            setSelectedPaymentId(null);

        } catch (err) {
            console.error('Error saving payment:', err);
            toast.error(err.response?.data?.message || 'Failed to process payment.');
        } finally {
            setProcessingPayment(false);
        }
    };

    // Checkbox Logic
    const toggleSelection = (id) => {
        const newSet = new Set(selectedInvoiceIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedInvoiceIds(newSet);
    };

    const toggleAll = () => {
        // Toggle all VISIBLE items only
        const allVisibleIds = filteredPayments.filter(p => !isPaidStatus(p.status)).map(p => p.id);
        const allSelected = allVisibleIds.every(id => selectedInvoiceIds.has(id));

        const newSet = new Set(selectedInvoiceIds);
        if (allSelected) {
            allVisibleIds.forEach(id => newSet.delete(id));
        } else {
            allVisibleIds.forEach(id => newSet.add(id));
        }
        setSelectedInvoiceIds(newSet);
    };

    const confirmCreateBatch = (count) => {
        return new Promise((resolve) => {
            const toastId = toast(
                <div className="flex flex-col space-y-4 p-2">
                    <p className="text-gray-800">
                        Create payment batch for <strong>{count}</strong> invoices?<br />
                        <span className="text-xs text-gray-500">This will group them for processing.</span>
                    </p>
                    <div className="flex justify-end space-x-3">
                        <button
                            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors"
                            onClick={() => { toast.dismiss(toastId); resolve(false); }}
                        >
                            Cancel
                        </button>
                        <button
                            className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm transition-colors"
                            onClick={() => { toast.dismiss(toastId); resolve(true); }}
                        >
                            Create Batch
                        </button>
                    </div>
                </div>,
                {
                    position: 'top-center',
                    autoClose: false,
                    closeOnClick: false,
                    draggable: false,
                    closeButton: false,
                    style: { minWidth: '350px', cursor: 'default' }
                }
            );
        });
    };

    const confirmProcessPayment = (amount) => {
        return new Promise((resolve) => {
            const toastId = toast(
                <div className="flex flex-col space-y-4 p-2">
                    <p className="text-gray-800">
                        Confirm payment of <strong>{formatCurrency(amount)}</strong>?<br />
                        <span className="text-xs text-gray-500">This will mark the invoice as paid.</span>
                    </p>
                    <div className="flex justify-end space-x-3">
                        <button
                            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors"
                            onClick={() => { toast.dismiss(toastId); resolve(false); }}
                        >
                            Cancel
                        </button>
                        <button
                            className="px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md shadow-sm transition-colors"
                            onClick={() => { toast.dismiss(toastId); resolve(true); }}
                        >
                            Confirm
                        </button>
                    </div>
                </div>,
                {
                    position: 'top-center',
                    autoClose: false,
                    closeOnClick: false,
                    draggable: false,
                    closeButton: false,
                    style: { minWidth: '350px', cursor: 'default' }
                }
            );
        });
    };

    const handleGenerateBatch = async () => {
        if (selectedInvoiceIds.size === 0) {
            toast.warning('Please select at least one invoice.');
            return;
        }

        const confirmed = await confirmCreateBatch(selectedInvoiceIds.size);
        if (!confirmed) return;

        setCreatingBatch(true);
        try {
            const invoiceIds = Array.from(selectedInvoiceIds);
            const createdBy = user?.name || 'Admin';
            await paymentBatchApi.createBatch(invoiceIds, createdBy);
            toast.success('Payment batch created successfully!');
            setSelectedInvoiceIds(new Set());
            // Redirect to batches page to see the new batch
            navigate('/payment-batches');
        } catch (err) {
            console.error('Failed to create batch', err);
            toast.error(`Failed to create payment batch: ${err.message || 'Unknown error'}`);
        } finally {
            setCreatingBatch(false);
        }
    };

    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        } catch (e) {
            return 'N/A';
        }
    };

    const filterOptions = [
        { value: 'approved', label: 'Approved (Ready to Pay)' },
        { value: 'invoice_paid', label: 'Invoice Paid' },
    ];

    const tableHeaders = [
        <div key="select-header" className="flex items-center">
            <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={filteredPayments.length > 0 && filteredPayments.every(p => isPaidStatus(p.status) || selectedInvoiceIds.has(p.id))}
                onChange={toggleAll}
                disabled={filterStatus !== 'approved'}
            />
        </div>,
        'Invoice #', 'Date Recvd', 'Invoice Date', 'Due Date', 'Vendor', 'Subtotal', 'Tax', 'Total', 'TDS', 'Invoice Payable', 'Action'
    ];

    const tableData = filteredPayments.map(payment => {
        const vendorName = vendorMap[payment.vendorId] || 'Loading...';
        const paid = isPaidStatus(payment.status);
        const inBatch = payment.status === 'in_payment_batch';

        return [
            <div key={`select-${payment.id}`} className="flex items-center">
                <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    checked={selectedInvoiceIds.has(payment.id)}
                    onChange={() => toggleSelection(payment.id)}
                    disabled={paid || inBatch}
                />
            </div>,
            payment.invoiceNumber || 'N/A',
            payment.dateReceived ? formatDate(payment.dateReceived) : 'N/A',
            payment.invoiceDate ? formatDate(payment.invoiceDate) : 'N/A',
            payment.dueDate ? formatDate(payment.dueDate) : 'N/A',
            <div className="max-w-[150px] text-xs leading-snug break-words whitespace-normal font-medium text-gray-900" key={`vendor-${payment.id}`}>
                {vendorName}
            </div>,
            payment.subtotal != null ? formatCurrency(payment.subtotal) : 'N/A',
            payment.tax != null ? formatCurrency(payment.tax) : 'N/A',
            <span className="font-bold text-gray-900" key={`total-${payment.id}`}>{formatCurrency(payment.amount)}</span>,
            <div key={`${payment.id}-tds`} className="text-gray-600">
                {payment.tdsAmount != null
                    ? `${formatCurrency(payment.tdsAmount)}${payment.tdsPercentage != null ? ` (${payment.tdsPercentage}%)` : ''}`
                    : 'N/A'}
            </div>,
            <div key={`${payment.id}-payable`} className="font-bold text-primary-600">
                {payment.payableAmount != null ? formatCurrency(payment.payableAmount) : 'N/A'}
            </div>,
            <div key={`${payment.id}-actions`} className="flex items-center justify-start space-x-2">
                {inBatch ? (
                    <span className="text-xs font-semibold bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Batch Created</span>
                ) : (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePaymentClick(payment)}
                        disabled={processingPayment || paid}
                        title={paid ? `Already paid` : "Mark this invoice as paid"}
                    >
                        {processingPayment && selectedPayment?.id === payment.id ? (
                            <I.Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : paid ? (
                            <I.CheckCheck className="w-4 h-4 mr-1 text-green-600" />
                        ) : (
                            <I.Check className="w-4 h-4 mr-1" />
                        )}
                        {processingPayment && selectedPayment?.id === payment.id ? 'Processing...' : paid ? 'Paid' : 'Mark as Paid'}
                    </Button>
                )}
            </div>
        ];
    });

    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-4 bg-gray-50 min-h-screen">
            <PageHeader
                title="Manage Invoice Payment"
                subtitle="Review invoices, generate payment batches, or mark single invoices as paid"
                actions={[
                    <Button key="refresh" variant="secondary" onClick={fetchPayments} disabled={loading} className="bg-white text-black border-gray-300">
                        <I.RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>,
                    <Button
                        key="create-batch"
                        variant="primary"
                        onClick={handleGenerateBatch}
                        disabled={creatingBatch || selectedInvoiceIds.size === 0}
                        className="bg-blue-600 text-white hover:bg-blue-700 shadow-lg"
                    >
                        {creatingBatch ? <I.Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <I.Layers className="w-4 h-4 mr-2" />}
                        {creatingBatch ? 'Creating...' : `Create Batch (${selectedInvoiceIds.size})`}
                    </Button>
                ]}
            />

            <Card className="shadow-sm border border-gray-200 bg-white overflow-hidden">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 border-b border-gray-100">
                    <div className="flex-1 min-w-0 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <I.Search className="w-4 h-4 text-gray-400" />
                        </span>
                        <input
                            type="text"
                            placeholder="Search by Invoice # or Vendor Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-10 py-2 h-11 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 sm:text-sm transition-all"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                            >
                                <I.X className="h-4 w-4" />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="w-full md:w-auto md:min-w-[240px]">
                            <Select
                                id="statusFilter"
                                value={filterStatus}
                                onChange={handleFilterChange}
                                leftIcon={<I.Filter className="w-4 h-4 text-gray-400" />}
                                options={filterOptions}
                            />
                        </div>

                        {(searchTerm || filterStatus !== 'approved') && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSearchTerm(''); setFilterStatus('approved'); setCurrentPage(0); }}
                                className="text-gray-500 hover:text-primary-600"
                            >
                                <I.X className="w-4 h-4 mr-1" /> Clear
                            </Button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading && payments.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24">
                            <LoadingSpinner size="lg" />
                            <span className="mt-4 text-lg text-gray-500 font-medium">Fetching payment records...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-24 px-4">
                            <div className="bg-red-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <I.AlertTriangle className="w-8 h-8 text-red-500" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2">System Error</h3>
                            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">{error}</p>
                            <Button onClick={fetchPayments} variant="outline" className="rounded-xl">Try Again</Button>
                        </div>
                    ) : filteredPayments.length === 0 ? (
                        <div className="text-center py-24 px-4">
                            <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <I.SearchX className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2">No Records Found</h3>
                        </div>
                    ) : (
                        <Table headers={tableHeaders} data={tableData} loading={loading} stickyFirstColumn={true} />
                    )}
                </div>

                {totalPages > 1 && !error && (
                    <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="text-xs sm:text-sm text-gray-500 font-medium">
                            Showing <span className="text-gray-900">{(currentPage * pageSize) + (filteredPayments.length > 0 ? 1 : 0)}</span> to {' '}
                            <span className="text-gray-900">{Math.min((currentPage * pageSize) + filteredPayments.length, totalPayments)}</span> of{' '}
                            <span className="text-gray-900">{totalPayments}</span> total invoices
                        </div>
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal isOpen={showPaymentModal} onClose={() => !processingPayment && setShowPaymentModal(false)} title="Confirm Payment Details">
                {selectedPayment && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-4 bg-primary-50/50 rounded-2xl border border-primary-100 shadow-sm">
                            <div>
                                <p className="text-[10px] font-bold text-primary-600 uppercase tracking-wider mb-0.5">Invoice Number</p>
                                <p className="font-mono text-base font-bold text-gray-900">{selectedPayment.invoiceNumber}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-primary-600 uppercase tracking-wider mb-0.5">Vendor Name</p>
                                <p className="text-base font-bold text-gray-900 truncate">{vendorMap[selectedPayment.vendorId] || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Total Amount</p>
                                <p className="text-sm font-bold text-gray-600">{formatCurrency(selectedPayment.amount)}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">TDS Deducted</p>
                                <p className="text-sm font-bold text-red-500">-{formatCurrency(selectedPayment.tdsAmount ?? 0)}</p>
                            </div>
                            <div className="col-span-2 mt-1 pt-2 border-t border-primary-100">
                                <p className="text-[10px] font-bold text-primary-700 uppercase tracking-wider mb-0.5">Final Amount Payable</p>
                                <p className="font-black text-2xl text-primary-600">{formatCurrency(selectedPayment.payableAmount ?? 0)}</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Payment Date & Time</label>
                                <input
                                    type="datetime-local"
                                    value={paymentDate}
                                    onChange={(e) => setPaymentDate(e.target.value)}
                                    className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all text-sm"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Method</label>
                                    <select
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-sm"
                                    >
                                        <option value="bank_transfer">Bank Transfer</option>
                                        <option value="credit_card">Credit Card</option>
                                        <option value="debit_card">Debit Card</option>
                                        <option value="upi">UPI</option>
                                        <option value="cheque">Cheque</option>
                                        <option value="cash">Cash</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Reference #</label>
                                    <input
                                        ref={referenceInputRef}
                                        type="text"
                                        placeholder="UTR / Ref ID"
                                        value={referenceNumber}
                                        onChange={(e) => { setReferenceNumber(e.target.value); referenceValueRef.current = e.target.value; }}
                                        className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-medium text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 mt-4">
                            <Button variant="secondary" onClick={() => setShowPaymentModal(false)} disabled={processingPayment} className="rounded-lg px-4 h-10 bg-white text-black border-gray-300 text-sm">
                                <I.X className="w-4 h-4 mr-1.5" />
                                Cancel
                            </Button>
                            <Button onClick={handlePaymentSubmit} variant="secondary" disabled={processingPayment} className="rounded-lg px-6 h-10 bg-white text-black font-bold border-black hover:bg-gray-50 shadow-md shadow-gray-200 text-sm">
                                {processingPayment ? <LoadingSpinner size="sm" className="mr-2" /> : <I.Check className="w-4 h-4 mr-2" />}
                                {processingPayment ? 'Processing...' : 'Confirm & Mark Paid'}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PaymentPage;
